import { Controller } from "@hotwired/stimulus"
import { showToast } from "./toast"

// Web-renderable image formats. HEIC/HEIF (iPhone) and other non-web formats
// upload to Filestack fine but then render as a broken/dead image, so we reject
// them up front with a clear message rather than saving an unusable record.
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/avif"]

export default class extends Controller {
  static targets = [
    "gallery", "uploadButton", "dropZone", "fileInput", "cameraInput", "status", "thumbStrip",
    "lightbox", "lightboxImage", "makeCoverBtn", "makeCoverLabel", "deleteBtn",
    "cropModal", "cropImage", "cropSaveBtn", "cropStatus"
  ]
  static values = {
    apiKey: String,
    uploadUrl: String // the owner's media collection path, e.g. /app/box_sets/1/media
  }

  connect() {
    if (!window.filestack && this.hasApiKeyValue) {
      this.loadFilestackScript()
    }
    // Preload the cropper so the "Edit" button is responsive when clicked.
    if (this.hasCropModalTarget) this.loadCropperAssets()
    // Listen for clipboard paste so users can paste images directly into the
    // drop zone — handy on macOS where copy-from-Finder is one keystroke.
    if (this.hasDropZoneTarget) {
      this.boundPaste = this.handlePaste.bind(this)
      document.addEventListener("paste", this.boundPaste)
    }
  }

  disconnect() {
    if (this.boundPaste) document.removeEventListener("paste", this.boundPaste)
  }

  loadFilestackScript() {
    if (document.querySelector('script[src*="filestackapi"]')) return

    const script = document.createElement("script")
    script.src = "https://static.filestackapi.com/filestack-js/3.x.x/filestack.min.js"
    script.async = true
    document.head.appendChild(script)
  }

  // Cropper.js (crop editor) loaded from a CDN on demand, mirroring how the
  // Filestack script is loaded — no bundler/npm dependency.
  loadCropperAssets() {
    if (!document.querySelector("link[data-cropper-css]")) {
      const link = document.createElement("link")
      link.rel = "stylesheet"
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.6.2/cropper.min.css"
      link.dataset.cropperCss = "1"
      document.head.appendChild(link)
    }
    if (!window.Cropper && !document.querySelector("script[data-cropper-js]")) {
      const script = document.createElement("script")
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.6.2/cropper.min.js"
      script.async = true
      script.dataset.cropperJs = "1"
      document.head.appendChild(script)
    }
  }

  openPicker(event) {
    event.preventDefault()

    if (!window.filestack) {
      showToast("File uploader is loading. Please try again.")
      return
    }

    const purpose = event.currentTarget.dataset.purpose || "photo"
    const client = filestack.init(this.apiKeyValue)

    const options = {
      accept: ["image/*", "application/pdf"],
      maxFiles: 10,
      transformations: {
        crop: true
      },
      onUploadDone: (result) => this.handleUploadDone(result, purpose)
    }

    client.picker(options).open()
  }

  async handleUploadDone(result, purpose) {
    if (result.filesUploaded.length === 0) return

    for (const file of result.filesUploaded) {
      const mediaType = file.mimetype.startsWith("image/") ? "image" :
                        file.mimetype === "application/pdf" ? "pdf" : "document"

      const data = {
        medium: {
          url: file.url,
          thumbnail_url: file.url + "?w=200&h=200&fit=crop",
          filename: file.filename,
          file_size: file.size,
          mime_type: file.mimetype,
          media_type: mediaType,
          purpose: purpose
        }
      }

      try {
        const response = await fetch(this.uploadUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify(data)
        })

        if (response.ok) {
          const json = await response.json()
          this.addImageToGallery(json, file)
        } else {
          const error = await response.json()
          console.error("Upload failed:", error)
        }
      } catch (error) {
        console.error("Upload error:", error)
      }
    }

    window.location.reload()
  }

  addImageToGallery(data, file) {
    if (!this.hasGalleryTarget) return

    const div = document.createElement("div")
    div.className = "relative group"
    div.innerHTML = `
      <img src="${file.url}" alt="${file.filename}" class="w-full h-32 object-cover rounded-lg">
      <div class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
        <span class="text-white text-sm">Uploaded</span>
      </div>
    `
    this.galleryTarget.appendChild(div)
  }

  async delete(event) {
    event.preventDefault()

    if (!confirm("Remove this image?")) return

    const url = event.currentTarget.dataset.url

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        event.currentTarget.closest("[data-media-item]").remove()
      }
    } catch (error) {
      console.error("Delete error:", error)
    }
  }

  // ----- Drop zone, paste, file-input — direct uploads without the picker -----

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("is-drag-over")
  }

  dragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("is-drag-over")
  }

  async drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("is-drag-over")
    const files = this.acceptImages(Array.from(event.dataTransfer?.files || []))
    if (files.length === 0) return
    await this.uploadFiles(files)
  }

  async chooseFiles(event) {
    const files = this.acceptImages(Array.from(event.target.files || []))
    event.target.value = "" // reset so picking the same file again still fires
    if (files.length === 0) return
    await this.uploadFiles(files)
  }

  openFilePicker(event) {
    event?.preventDefault()
    if (this.hasFileInputTarget) this.fileInputTarget.click()
  }

  // Opens the device camera (mobile/iPad) via the capture input; the captured
  // photo flows through the same chooseFiles → uploadFiles pipeline.
  takePhoto(event) {
    event?.preventDefault()
    if (this.hasCameraInputTarget) this.cameraInputTarget.click()
  }

  async handlePaste(event) {
    if (!event.clipboardData) return
    // Don't intercept paste when the user is typing in a real input/textarea
    const tag = (event.target.tagName || "").toLowerCase()
    if (["input", "textarea"].includes(tag) || event.target.isContentEditable) return
    const raw = Array.from(event.clipboardData.items)
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter(Boolean)
    if (raw.length === 0) return
    const files = this.acceptImages(raw)
    if (files.length === 0) return
    await this.uploadFiles(files)
  }

  // True only for image formats browsers can actually display. Falls back to
  // the file extension when the browser doesn't report a MIME type (some do
  // this for HEIC), and always rejects HEIC/HEIF explicitly.
  isAcceptedImage(file) {
    const type = (file.type || "").toLowerCase()
    const name = (file.name || "").toLowerCase()
    if (type.includes("heic") || type.includes("heif") || /\.(heic|heif)$/.test(name)) return false
    if (ACCEPTED_IMAGE_TYPES.includes(type)) return true
    if (!type) return /\.(jpe?g|png|gif|webp|avif)$/.test(name)
    return false
  }

  // Splits a dropped/pasted/picked file list into the ones we can use, warning
  // the user about anything rejected (e.g. an iPhone .heic) so it's not silent.
  acceptImages(files) {
    const accepted = []
    const rejected = []
    files.forEach((f) => (this.isAcceptedImage(f) ? accepted : rejected).push(f))
    if (rejected.length) {
      const names = rejected.map((f) => f.name || "image").join(", ")
      showToast(`Skipped ${rejected.length} unsupported file${rejected.length === 1 ? "" : "s"} (${names}). Use JPG, PNG, GIF, WebP, or AVIF.`)
    }
    return accepted
  }

  async uploadFiles(files) {
    if (!window.filestack || !this.hasApiKeyValue) {
      showToast("File uploader is loading. Please wait a moment and try again.")
      return
    }
    const client = filestack.init(this.apiKeyValue)
    let done = 0
    this.setStatus(`Uploading ${files.length} image${files.length === 1 ? "" : "s"}…`)
    for (const file of files) {
      try {
        const uploaded = await client.upload(file)
        await this.persistMedium(uploaded, file, "photo")
        done += 1
        this.setStatus(`Uploaded ${done} of ${files.length}…`)
      } catch (err) {
        console.error("Image upload failed", err)
      }
    }
    this.setStatus(`Done — ${done} of ${files.length} uploaded.`)
    setTimeout(() => this.setStatus(""), 4000)
  }

  // Saves a single Filestack-uploaded asset to our Media table.
  async persistMedium(uploaded, originalFile, purpose) {
    const data = {
      medium: {
        url: uploaded.url,
        thumbnail_url: uploaded.url + "?w=200&h=200&fit=crop",
        filename: uploaded.filename || originalFile.name,
        file_size: uploaded.size || originalFile.size,
        mime_type: uploaded.mimetype || originalFile.type,
        media_type: "image",
        purpose: purpose
      }
    }
    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // Without this, Rails' respond_to picks format.html (declared first
        // in BookMediaController#create) and returns a redirect — response.json()
        // then throws, the thumb never appends, and the image only shows after
        // a manual refresh. Asking for JSON fixes that.
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(data)
    })
    if (response.ok) {
      const json = await response.json()
      this.appendThumb(uploaded.url, json)
    } else {
      console.error("Persist failed", await response.text())
    }
  }

  // Append a just-uploaded photo to the strip as a real lightbox thumbnail —
  // same markup the server renders — so it's immediately clickable for
  // Make-Cover / Delete without a page refresh.
  appendThumb(uploadedUrl, json) {
    if (!this.hasThumbStripTarget || !json || !json.id) return
    const id = json.id
    const thumbSrc = json.thumbnail_url || uploadedUrl
    const isCover = json.is_cover === true // first image of a coverless book auto-becomes the cover
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "relative block w-16 h-16 rounded-lg overflow-hidden border border-beige-200 shadow-sm hover:shadow-md hover:scale-105 transition cursor-zoom-in"
    btn.setAttribute("data-action", "click->book-media#openLightbox")
    btn.dataset.bookMediaUrlParam = uploadedUrl
    btn.dataset.bookMediaSetPrimaryUrlParam = `${this.uploadUrlValue}/${id}/set_primary`
    btn.dataset.bookMediaDeleteUrlParam = `${this.uploadUrlValue}/${id}`
    btn.dataset.bookMediaIsCoverParam = isCover ? "true" : "false"
    btn.innerHTML = `<img src="${thumbSrc}" alt="" class="w-full h-full object-cover">` +
      (isCover ? `<span class="absolute bottom-0 inset-x-0 bg-amber-600/90 text-white text-[10px] font-medium text-center py-0.5">Cover</span>` : "")
    this.thumbStripTarget.appendChild(btn)
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  async setPrimary(event) {
    event.preventDefault()

    const url = event.currentTarget.dataset.url

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        window.location.reload()
      }
    } catch (error) {
      console.error("Set primary error:", error)
    }
  }

  // ----- Lightbox (clicking a thumbnail in the Images card) -----

  openLightbox(event) {
    event.preventDefault()
    if (!this.hasLightboxTarget) return

    const url = event.currentTarget.dataset.bookMediaUrlParam
    const setPrimaryUrl = event.currentTarget.dataset.bookMediaSetPrimaryUrlParam
    const deleteUrl = event.currentTarget.dataset.bookMediaDeleteUrlParam
    const isCover = event.currentTarget.dataset.bookMediaIsCoverParam === "true"
    if (!url) return

    // Remember which thumbnail is open so Delete can remove it from the strip
    // without a full page refresh.
    this._openThumbEl = event.currentTarget

    if (this.hasLightboxImageTarget) this.lightboxImageTarget.src = url
    if (this.hasMakeCoverBtnTarget) {
      this.makeCoverBtnTarget.dataset.setPrimaryUrl = setPrimaryUrl || ""
      // Hide the button if this photo is already the cover.
      this.makeCoverBtnTarget.classList.toggle("hidden", isCover)
    }
    if (this.hasMakeCoverLabelTarget) {
      this.makeCoverLabelTarget.textContent = isCover ? "Already the cover" : "Make Cover Image"
    }
    if (this.hasDeleteBtnTarget) {
      this.deleteBtnTarget.dataset.deleteUrl = deleteUrl || ""
    }
    // The medium's REST path (same path as delete) — the cropper PATCHes it
    // with the swapped-in cropped image.
    this._currentMediumPath = deleteUrl || ""

    this.lightboxTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  lightboxBackdrop(event) {
    // Only close when the click landed on the backdrop itself, not children.
    if (event.target === this.lightboxTarget) this.closeLightbox()
  }

  closeLightbox() {
    if (!this.hasLightboxTarget) return
    this.lightboxTarget.classList.add("hidden")
    document.body.style.overflow = ""
    if (this.hasLightboxImageTarget) this.lightboxImageTarget.src = ""
  }

  async makeCover(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.setPrimaryUrl
    if (!url) return

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        window.location.reload()
      } else {
        console.error("Make cover failed:", await response.text())
      }
    } catch (error) {
      console.error("Make cover error:", error)
    }
  }

  async deletePhoto(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.deleteUrl
    if (!url) return
    if (!window.confirm("Delete this image? This can't be undone.")) return

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        // Remove the thumbnail from the strip and close the lightbox — no
        // refresh needed.
        this._openThumbEl?.remove()
        this._openThumbEl = null
        this.closeLightbox()
      } else {
        console.error("Delete failed:", await response.text())
        alert("Couldn't delete that image.")
      }
    } catch (error) {
      console.error("Delete error:", error)
    }
  }

  // ----- Crop editor (Cropper.js) -----

  openCropper(event) {
    event.preventDefault()
    if (!this.hasCropModalTarget || !this.hasCropImageTarget) return
    if (!window.Cropper) {
      showToast("Image editor is still loading — try again in a moment.")
      return
    }
    const src = this.hasLightboxImageTarget ? this.lightboxImageTarget.src : ""
    if (!src) return

    this.setCropStatus("")
    this.cropModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    this.cropImageTarget.src = src
    this._cropper?.destroy()
    // checkCrossOrigin lets Cropper load the Filestack image with CORS so the
    // canvas isn't tainted and we can export the crop.
    this._cropper = new Cropper(this.cropImageTarget, {
      viewMode: 1,
      autoCropArea: 1,
      background: false,
      checkCrossOrigin: true
    })
  }

  cancelCrop() {
    this._cropper?.destroy()
    this._cropper = null
    if (this.hasCropModalTarget) this.cropModalTarget.classList.add("hidden")
    // The lightbox is still open underneath, so keep scroll locked if so.
    const lightboxOpen = this.hasLightboxTarget && !this.lightboxTarget.classList.contains("hidden")
    document.body.style.overflow = lightboxOpen ? "hidden" : ""
    this.setCropStatus("")
  }

  async saveCrop(event) {
    event.preventDefault()
    if (!this._cropper) return
    if (!window.filestack || !this.hasApiKeyValue) {
      this.setCropStatus("Uploader still loading — try again.")
      return
    }
    if (!this._currentMediumPath) {
      this.setCropStatus("Couldn't find the photo to update.")
      return
    }

    this.setCropStatus("Saving…")
    if (this.hasCropSaveBtnTarget) this.cropSaveBtnTarget.disabled = true

    let canvas
    try {
      canvas = this._cropper.getCroppedCanvas({ maxWidth: 4096, maxHeight: 4096 })
    } catch (e) {
      console.error("Crop read failed", e)
    }
    if (!canvas) {
      this.setCropStatus("Couldn't read the image. Try re-uploading it instead.")
      if (this.hasCropSaveBtnTarget) this.cropSaveBtnTarget.disabled = false
      return
    }

    canvas.toBlob(async (blob) => {
      if (!blob) {
        this.setCropStatus("Crop failed.")
        if (this.hasCropSaveBtnTarget) this.cropSaveBtnTarget.disabled = false
        return
      }
      try {
        const client = filestack.init(this.apiKeyValue)
        const uploaded = await client.upload(blob)
        const resp = await fetch(this._currentMediumPath, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            medium: { url: uploaded.url, thumbnail_url: uploaded.url + "?w=200&h=200&fit=crop" }
          })
        })
        if (resp.ok) {
          window.location.reload()
        } else {
          console.error("Crop save failed", await resp.text())
          this.setCropStatus("Save failed.")
          if (this.hasCropSaveBtnTarget) this.cropSaveBtnTarget.disabled = false
        }
      } catch (e) {
        console.error("Crop upload failed", e)
        this.setCropStatus("Save failed.")
        if (this.hasCropSaveBtnTarget) this.cropSaveBtnTarget.disabled = false
      }
    }, "image/jpeg", 0.92)
  }

  setCropStatus(text) {
    if (this.hasCropStatusTarget) this.cropStatusTarget.textContent = text
  }
}
